import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { AlertTriangle, Trash2, User, Server, Database, Loader2, CheckCircle, XCircle, Users } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useSupprimerToutesDonneesMutation, useStatistiquesDonneesQuery } from '../../services/coreApi';
import { useViderBaseMutation } from '../../services/etudiantsApi';
import type { SuppressionDonneesResponse } from '../../services/coreApi';

const CONFIRMATION_TEXT = 'SUPPRIMER_TOUTES_LES_DONNEES';
const CONFIRMATION_TEXT_ETUDIANTS = 'SUPPRIMER_TOUS_LES_ETUDIANTS';

const DonneesTab: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [supprimerDonnees, { isLoading }] = useSupprimerToutesDonneesMutation();
  const { data: statsData } = useStatistiquesDonneesQuery();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteResult, setDeleteResult] = useState<SuppressionDonneesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Suppression ciblée étudiants uniquement
  const [viderEtudiants, { isLoading: isLoadingEtudiants }] = useViderBaseMutation();
  const [showDeleteEtudiantsModal, setShowDeleteEtudiantsModal] = useState(false);
  const [confirmEtudiantsText, setConfirmEtudiantsText] = useState('');
  const [etudiantsResult, setEtudiantsResult] = useState<{ nb_supprimes: number; message: string } | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      const result = await supprimerDonnees({ confirmation: confirmText }).unwrap();
      setDeleteResult(result);
      setShowDeleteModal(false);
      setConfirmText('');
    } catch (err: any) {
      setError(err.data?.error || err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteEtudiants = async () => {
    setError(null);
    try {
      const result = await viderEtudiants().unwrap();
      setEtudiantsResult(result);
      setShowDeleteEtudiantsModal(false);
      setConfirmEtudiantsText('');
    } catch (err: any) {
      setError(err.data?.error || err.data?.detail || 'Erreur lors de la suppression des étudiants');
    }
  };

  const ROLE_LABELS: Record<string, string> = {
    super_admin: 'Super Administrateur',
    admin: 'Administrateur',
    agent: 'Agent de Scolarité',
    consultation: 'Consultation Seule',
  };

  return (
    <div className="space-y-6">
      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Session */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <User size={18} className="text-blue-500" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Session</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Statut</span>
              <Badge variant="success" size="sm">Connecté</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Utilisateur</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.username}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Rôle</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{ROLE_LABELS[user?.role || ''] || user?.role}</span>
            </div>
          </div>
        </div>

        {/* Application */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Server size={18} className="text-purple-500" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Application</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Nom</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Gestion Notes 2i</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Type</span>
              <span className="text-sm text-gray-700 dark:text-gray-300">LMD Académique</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Stack</span>
              <span className="text-sm text-gray-700 dark:text-gray-300">Django + React</span>
            </div>
          </div>
        </div>

        {/* Base de données */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Database size={18} className="text-green-500" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Base de données</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Moteur</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">PostgreSQL</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Statut</span>
              <Badge variant="success" size="sm">Connecté</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Backup auto</span>
              <span className="text-sm text-gray-700 dark:text-gray-300">Avant suppression</span>
            </div>
          </div>
        </div>
      </div>

      {/* Compteurs de données */}
      {statsData && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Database size={18} className="text-blue-500" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Données dans le schema actif</h4>
            <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full font-mono">{statsData.schema}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(statsData.compteurs).map(([table, info]) => (
              <div key={table} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {info.count !== null ? info.count.toLocaleString() : '—'}
                </div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{info.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete result */}
      {deleteResult && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-300">Données supprimées avec succès</h4>
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                {deleteResult.total_deleted} enregistrements supprimés. Backup créé : {deleteResult.backup_filename}
              </p>
            </div>
          </div>
        </div>
      )}

      {etudiantsResult && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-300">Étudiants supprimés</h4>
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">{etudiantsResult.message}</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-300">Erreur</h4>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-red-300 dark:border-red-700 overflow-hidden">
        <div className="bg-red-50 dark:bg-red-900/20 px-5 py-3 border-b border-red-200 dark:border-red-700">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600 dark:text-red-400" />
            <h3 className="font-semibold text-red-800 dark:text-red-300">Zone dangereuse</h3>
          </div>
        </div>
        <div className="divide-y divide-red-100 dark:divide-red-900/30">
          {/* Bloc 1 : Étudiants uniquement */}
          <div className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Supprimer tous les étudiants</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Supprime uniquement les fiches étudiants (matricules, infos personnelles, inscriptions).
                  Les notes, matières et copies sont conservées, mais deviendront orphelines si leurs étudiants sont supprimés.
                </p>
                <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/10 rounded text-xs text-amber-700 dark:text-amber-400">
                  <strong>Utilité :</strong> nettoyage d'un import Excel raté avant de ré-importer la liste corrigée.
                </div>
              </div>
              <Button
                variant="danger"
                icon={<Users size={16} />}
                onClick={() => { setShowDeleteEtudiantsModal(true); setError(null); }}
              >
                Supprimer étudiants
              </Button>
            </div>
          </div>

          {/* Bloc 2 : Tout */}
          <div className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Supprimer toutes les données</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Supprime toutes les données de l'application (étudiants, notes, matières, copies, etc.).
                  Les comptes utilisateurs et le journal d'audit sont conservés.
                  Un backup automatique est créé avant la suppression.
                </p>
                <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/10 rounded text-xs text-red-700 dark:text-red-400">
                  <strong>Attention :</strong> Cette action est irréversible. Assurez-vous d'avoir une sauvegarde récente.
                </div>
              </div>
              <Button
                variant="danger"
                icon={<Trash2 size={16} />}
                onClick={() => { setShowDeleteModal(true); setError(null); }}
              >
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal confirmation suppression étudiants */}
      {showDeleteEtudiantsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <Users size={24} className="text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Supprimer tous les étudiants</h3>
              </div>

              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <p>Vous êtes sur le point de <strong className="text-red-600 dark:text-red-400">supprimer toutes les fiches étudiants</strong>.</p>
                <p>Les notes, matières et copies ne sont pas touchées mais deviendront orphelines. Utilisable typiquement pour ré-importer une liste après correction du fichier Excel.</p>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tapez <code className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-mono">{CONFIRMATION_TEXT_ETUDIANTS}</code> pour confirmer
                </label>
                <input
                  type="text"
                  value={confirmEtudiantsText}
                  onChange={(e) => setConfirmEtudiantsText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
                  placeholder={CONFIRMATION_TEXT_ETUDIANTS}
                />
              </div>

              {error && (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => { setShowDeleteEtudiantsModal(false); setConfirmEtudiantsText(''); setError(null); }}>
                  Annuler
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeleteEtudiants}
                  disabled={confirmEtudiantsText !== CONFIRMATION_TEXT_ETUDIANTS || isLoadingEtudiants}
                  icon={isLoadingEtudiants ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                >
                  {isLoadingEtudiants ? 'Suppression...' : 'Confirmer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertTriangle size={24} className="text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirmation requise</h3>
              </div>

              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <p>Vous êtes sur le point de <strong className="text-red-600 dark:text-red-400">supprimer toutes les données</strong> de l'application :</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Étudiants et inscriptions</li>
                  <li>Notes de devoirs et examens</li>
                  <li>Matières et unités d'enseignement</li>
                  <li>Copies et résultats OCR</li>
                  <li>Résultats ECUE, UE, Semestre</li>
                  <li>Demandes de rachat</li>
                  <li>Relevés de notes</li>
                </ul>
                <p className="font-medium text-gray-700 dark:text-gray-300">
                  Un backup sera automatiquement créé avant la suppression.
                </p>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tapez <code className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-mono">{CONFIRMATION_TEXT}</code> pour confirmer
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
                  placeholder={CONFIRMATION_TEXT}
                />
              </div>

              {error && (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-400">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => { setShowDeleteModal(false); setConfirmText(''); setError(null); }}>
                  Annuler
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={confirmText !== CONFIRMATION_TEXT || isLoading}
                  icon={isLoading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                >
                  {isLoading ? 'Suppression...' : 'Confirmer la suppression'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonneesTab;
